import axios from 'axios';
import { createFFmpeg, fetchFile } from '@ffmpeg/ffmpeg'

const baseURL = process.env.VUE_APP_BACKEND_URL || 'http://localhost:8787';

const api = axios.create({
    baseURL: baseURL,
});

// Utility function to get the token from local storage
const getAuthToken = () => {
    return localStorage.getItem('authToken');
};

api.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

const ffmpeg = createFFmpeg({ log: true });

export const convertToMP3 = async (audioFile: File): Promise<File|undefined> => {
    try {
        if (!ffmpeg.isLoaded()) {
            await ffmpeg.load();
        }

        const inputFileName = 'input.' + audioFile.name.split('.').pop();
        const outputFileName = 'output.mp3';

        await ffmpeg.FS('writeFile', inputFileName, await fetchFile(audioFile));

        // Run the FFmpeg command and check for errors
        try {
            await ffmpeg.run('-i', inputFileName, outputFileName);
        } catch (runError) {
            console.error("FFmpeg run error:", runError);
        }

        // Try to read the output file
        let data: any;
        try {
            data = ffmpeg.FS('readFile', outputFileName);
            if (!data) {
                throw new Error("readFile returned null or undefined"); // Explicit check
            }
        } catch (readError) {
            console.error("FFmpeg readFile error:", readError);
        }

        if (!data) {
            throw new Error("readFile did not return data"); // Ensure not void
        }

        ffmpeg.FS('unlink', inputFileName);
        ffmpeg.FS('unlink', outputFileName);

        const mp3Blob = new Blob([data.buffer], { type: 'audio/mpeg' });
        const mp3File = new File([mp3Blob], outputFileName, { type: 'audio/mpeg' });

        return mp3File;
    } catch (error) {
        console.error("Error converting to MP3:", error);
    }
};

export const sendAudio = async (audioFile: File) => {
    const formData = new FormData();
    // console.log(audioFile.type);
    
    const mp3 = audioFile.type === "audio/mpeg" ? audioFile : await convertToMP3(audioFile);
    if (mp3) {
        formData.append('audio', mp3);
    }

    try {
        const response = await api.post('/api', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data.text; // Return only the 'text' attribute
    } catch (error: any) {
        // Handle error (e.g., token expired, invalid token)
        console.error('Error uploading audio:', error);
        throw new Error(error.response?.data?.message || 'Failed to upload audio');
    }
};

